import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircleIcon, UploadCloudIcon, FileTextIcon, UserIcon, CalendarIcon, ActivityIcon, AlertCircleIcon, ChevronRightIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface FutureScholarClaimProps {
  navigate: (screen: string) => void;
}
export function FutureScholarClaim({
  navigate
}: FutureScholarClaimProps) {
  const [step, setStep] = useState(0);
  const [claimType, setClaimType] = useState<'death' | 'disability' | null>(null);
  // Form State
  const [formData, setFormData] = useState({
    deceasedName: '',
    dateOfDeath: '',
    relationship: '',
    disabilityDesc: '',
    incidentDate: '',
    medicalStatus: ''
  });
  // Upload State
  const [uploads, setUploads] = useState({
    deathCert: false,
    validId: false,
    medicalReport: false,
    disabilityCert: false
  });
  const [acceptedDeclaration, setAcceptedDeclaration] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleNext = () => {
    if (step === 3) {
      setIsSubmitting(true);
      setTimeout(() => {
        setIsSubmitting(false);
        setStep(4);
      }, 2000);
    } else {
      setStep(prev => prev + 1);
    }
  };
  const renderStepIndicator = () => {
    if (step >= 4) return null;
    const steps = ['Type', 'Details', 'Uploads', 'Review'];
    return <div className="flex items-center justify-between mb-8 px-2 relative" data-id="element-1759">
        <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-1 bg-gray-200 -z-10 rounded-full" data-id="element-1760"></div>
        <motion.div className="absolute left-6 top-1/2 -translate-y-1/2 h-1 bg-[#2BA89D] -z-10 rounded-full" animate={{
        width: `${step / (steps.length - 1) * 100}%`
      }} transition={{
        duration: 0.3
      }} data-id="element-1761" />
        {steps.map((s, i) => <div key={s} className="flex flex-col items-center gap-2 bg-slate-50 px-1" data-id="element-1762">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${i < step ? 'bg-[#2BA89D] border-[#2BA89D] text-white' : i === step ? 'bg-white border-[#2BA89D] text-[#2BA89D]' : 'bg-white border-gray-300 text-gray-400'}`} data-id="element-1763">
              {i < step ? <CheckCircleIcon className="w-5 h-5" data-id="element-1764" /> : i + 1}
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${i <= step ? 'text-[#0D3B3B]' : 'text-gray-400'}`} data-id="element-1765">
              {s}
            </span>
          </div>)}
      </div>;
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50 flex flex-col h-full" data-id="element-1766">
      <TopBar title={step >= 4 ? 'Claim Status' : 'Make a Claim'} showBack onBack={() => {
      if (step > 0 && step < 4) setStep(step - 1);else navigate('policies');
    }} data-id="element-1767" />

      <div className="px-5 pt-6 pb-8 flex-1 flex flex-col" data-id="element-1768">
        {renderStepIndicator()}

        <AnimatePresence mode="wait" data-id="element-1769">
          {/* STEP 0: Claim Type Selection */}
          {step === 0 && <motion.div key="step0" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1" data-id="element-1770">
              <h2 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1771">
                Select Claim Type
              </h2>
              <p className="text-sm text-gray-500 mb-8" data-id="element-1772">
                What type of claim are you making for your Future Scholar Fund
                policy?
              </p>

              <div className="space-y-4" data-id="element-1773">
                <button onClick={() => setClaimType('death')} className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-center gap-4 ${claimType === 'death' ? 'border-[#2BA89D] bg-[#2BA89D]/5' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-1774">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${claimType === 'death' ? 'bg-[#2BA89D] text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-1775">
                    <UserIcon className="w-6 h-6" data-id="element-1776" />
                  </div>
                  <div className="flex-1" data-id="element-1777">
                    <h3 className="font-bold text-[#0D3B3B] text-lg" data-id="element-1778">
                      Death Claim
                    </h3>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1779">
                      Claim due to the passing of the policyholder.
                    </p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${claimType === 'death' ? 'border-[#2BA89D]' : 'border-gray-300'}`} data-id="element-1780">
                    {claimType === 'death' && <div className="w-3 h-3 bg-[#2BA89D] rounded-full" data-id="element-1781" />}
                  </div>
                </button>

                <button onClick={() => setClaimType('disability')} className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-center gap-4 ${claimType === 'disability' ? 'border-[#2BA89D] bg-[#2BA89D]/5' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-1782">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${claimType === 'disability' ? 'bg-[#2BA89D] text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-1783">
                    <ActivityIcon className="w-6 h-6" data-id="element-1784" />
                  </div>
                  <div className="flex-1" data-id="element-1785">
                    <h3 className="font-bold text-[#0D3B3B] text-lg" data-id="element-1786">
                      Permanent Disability
                    </h3>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1787">
                      Claim due to permanent injury or disability.
                    </p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${claimType === 'disability' ? 'border-[#2BA89D]' : 'border-gray-300'}`} data-id="element-1788">
                    {claimType === 'disability' && <div className="w-3 h-3 bg-[#2BA89D] rounded-full" data-id="element-1789" />}
                  </div>
                </button>
              </div>

              <div className="mt-auto pt-8" data-id="element-1790">
                <button onClick={handleNext} disabled={!claimType} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${claimType ? 'bg-[#0D3B3B] text-[#FFD700] shadow-lg shadow-[#0D3B3B]/20 active:scale-95' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-1791">
                  Continue
                </button>
              </div>
            </motion.div>}

          {/* STEP 1: Form Fields */}
          {step === 1 && <motion.div key="step1" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-1792">
              <h2 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1793">
                Claim Details
              </h2>
              <p className="text-sm text-gray-500 mb-8" data-id="element-1794">
                Please provide the required details for the{' '}
                {claimType === 'death' ? 'death' : 'disability'} claim.
              </p>

              <div className="space-y-5 flex-1" data-id="element-1795">
                {claimType === 'death' ? <>
                    <div data-id="element-1796">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1797">
                        Deceased Full Name
                      </label>
                      <input type="text" value={formData.deceasedName} onChange={e => setFormData({
                  ...formData,
                  deceasedName: e.target.value
                })} placeholder="Enter full name" className="w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]" data-id="element-1798" />
                    </div>
                    <div data-id="element-1799">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1800">
                        Date of Death
                      </label>
                      <div className="relative" data-id="element-1801">
                        <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1802" />
                        <input type="date" value={formData.dateOfDeath} onChange={e => setFormData({
                    ...formData,
                    dateOfDeath: e.target.value
                  })} className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]" data-id="element-1803" />
                      </div>
                    </div>
                    <div data-id="element-1804">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1805">
                        Relationship to Policyholder
                      </label>
                      <select value={formData.relationship} onChange={e => setFormData({
                  ...formData,
                  relationship: e.target.value
                })} className="w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]" data-id="element-1806">
                        <option value="" disabled data-id="element-1807">
                          Select relationship
                        </option>
                        <option value="spouse" data-id="element-1808">Spouse</option>
                        <option value="child" data-id="element-1809">Child</option>
                        <option value="parent" data-id="element-1810">Parent</option>
                        <option value="sibling" data-id="element-1811">Sibling</option>
                        <option value="other" data-id="element-1812">Other</option>
                      </select>
                    </div>
                  </> : <>
                    <div data-id="element-1813">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1814">
                        Injury/Disability Description
                      </label>
                      <textarea value={formData.disabilityDesc} onChange={e => setFormData({
                  ...formData,
                  disabilityDesc: e.target.value
                })} placeholder="Describe the nature of the disability" rows={3} className="w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D] resize-none" data-id="element-1815" />
                    </div>
                    <div data-id="element-1816">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1817">
                        Incident Date
                      </label>
                      <div className="relative" data-id="element-1818">
                        <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1819" />
                        <input type="date" value={formData.incidentDate} onChange={e => setFormData({
                    ...formData,
                    incidentDate: e.target.value
                  })} className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]" data-id="element-1820" />
                      </div>
                    </div>
                    <div data-id="element-1821">
                      <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-1822">
                        Current Medical Status
                      </label>
                      <input type="text" value={formData.medicalStatus} onChange={e => setFormData({
                  ...formData,
                  medicalStatus: e.target.value
                })} placeholder="e.g., Undergoing treatment, Discharged" className="w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2BA89D]" data-id="element-1823" />
                    </div>
                  </>}
              </div>

              <div className="mt-8" data-id="element-1824">
                <button onClick={handleNext} disabled={claimType === 'death' ? !formData.deceasedName || !formData.dateOfDeath || !formData.relationship : !formData.disabilityDesc || !formData.incidentDate || !formData.medicalStatus} className="w-full py-4 rounded-xl font-bold text-lg bg-[#0D3B3B] text-[#FFD700] shadow-lg shadow-[#0D3B3B]/20 active:scale-95 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none transition-all" data-id="element-1825">
                  Continue
                </button>
              </div>
            </motion.div>}

          {/* STEP 2: Document Upload */}
          {step === 2 && <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-1826">
              <h2 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1827">
                Upload Documents
              </h2>
              <p className="text-sm text-gray-500 mb-6" data-id="element-1828">
                Please upload clear copies of the following required documents.
              </p>

              <div className="space-y-4 flex-1" data-id="element-1829">
                {claimType === 'death' ? <>
                    <UploadItem title="Death Certificate" desc="Official death certificate" uploaded={uploads.deathCert} onUpload={() => setUploads({
                ...uploads,
                deathCert: true
              })} data-id="element-1830" />
                    <UploadItem title="Valid ID" desc="Deceased's valid ID" uploaded={uploads.validId} onUpload={() => setUploads({
                ...uploads,
                validId: true
              })} data-id="element-1831" />
                    <UploadItem title="Medical Reports" desc="Hospital/Doctor's report" uploaded={uploads.medicalReport} onUpload={() => setUploads({
                ...uploads,
                medicalReport: true
              })} data-id="element-1832" />
                  </> : <>
                    <UploadItem title="Disability Certificate" desc="Official medical certificate" uploaded={uploads.disabilityCert} onUpload={() => setUploads({
                ...uploads,
                disabilityCert: true
              })} data-id="element-1833" />
                    <UploadItem title="Valid ID" desc="Your valid ID" uploaded={uploads.validId} onUpload={() => setUploads({
                ...uploads,
                validId: true
              })} data-id="element-1834" />
                    <UploadItem title="Medical Reports" desc="Hospital/Doctor's report" uploaded={uploads.medicalReport} onUpload={() => setUploads({
                ...uploads,
                medicalReport: true
              })} data-id="element-1835" />
                  </>}
              </div>

              <div className="mt-8" data-id="element-1836">
                <button onClick={handleNext} disabled={claimType === 'death' ? !uploads.deathCert || !uploads.validId || !uploads.medicalReport : !uploads.disabilityCert || !uploads.validId || !uploads.medicalReport} className="w-full py-4 rounded-xl font-bold text-lg bg-[#0D3B3B] text-[#FFD700] shadow-lg shadow-[#0D3B3B]/20 active:scale-95 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none transition-all" data-id="element-1837">
                  Continue
                </button>
              </div>
            </motion.div>}

          {/* STEP 3: Review */}
          {step === 3 && <motion.div key="step3" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-1838">
              <h2 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1839">
                Review Claim
              </h2>
              <p className="text-sm text-gray-500 mb-6" data-id="element-1840">
                Please review your claim details before submitting.
              </p>

              <div className="space-y-6 flex-1" data-id="element-1841">
                {/* Summary */}
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-1842">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-1843">
                    Claim Summary
                  </h3>
                  <div className="space-y-3" data-id="element-1844">
                    <div className="flex justify-between" data-id="element-1845">
                      <span className="text-sm text-gray-500" data-id="element-1846">Claim Type</span>
                      <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-1847">
                        {claimType === 'death' ? 'Death Claim' : 'Permanent Disability'}
                      </span>
                    </div>
                    {claimType === 'death' ? <>
                        <div className="flex justify-between" data-id="element-1848">
                          <span className="text-sm text-gray-500" data-id="element-1849">
                            Deceased Name
                          </span>
                          <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-1850">
                            {formData.deceasedName}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-1851">
                          <span className="text-sm text-gray-500" data-id="element-1852">
                            Date of Death
                          </span>
                          <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-1853">
                            {formData.dateOfDeath}
                          </span>
                        </div>
                      </> : <>
                        <div className="flex justify-between" data-id="element-1854">
                          <span className="text-sm text-gray-500" data-id="element-1855">
                            Incident Date
                          </span>
                          <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-1856">
                            {formData.incidentDate}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-1857">
                          <span className="text-sm text-gray-500" data-id="element-1858">
                            Medical Status
                          </span>
                          <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-1859">
                            {formData.medicalStatus}
                          </span>
                        </div>
                      </>}
                  </div>
                </div>

                {/* Uploaded Files */}
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-1860">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-1861">
                    Uploaded Files
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-1862">
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-bold" data-id="element-1863">
                      <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1864" /> Valid ID
                    </span>
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-bold" data-id="element-1865">
                      <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1866" /> Medical Report
                    </span>
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-bold" data-id="element-1867">
                      <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1868" />{' '}
                      {claimType === 'death' ? 'Death Cert' : 'Disability Cert'}
                    </span>
                  </div>
                </div>

                {/* Payout Destination */}
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-1869">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-1870">
                    Payout Destination
                  </h3>
                  <div className="flex items-center gap-3" data-id="element-1871">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-[#0D3B3B]" data-id="element-1872">
                      <UserIcon className="w-5 h-5" data-id="element-1873" />
                    </div>
                    <div data-id="element-1874">
                      <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-1875">
                        Skydd Wallet
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1876">
                        Default payout destination
                      </p>
                    </div>
                  </div>
                </div>

                {/* Declaration */}
                <label className="flex items-start gap-3 p-4 bg-amber-50 rounded-2xl cursor-pointer border border-amber-100" data-id="element-1877">
                  <input type="checkbox" checked={acceptedDeclaration} onChange={e => setAcceptedDeclaration(e.target.checked)} className="mt-1 w-5 h-5 rounded border-amber-300 text-amber-500 focus:ring-amber-500" data-id="element-1878" />
                  <span className="text-xs text-amber-800 font-medium leading-relaxed" data-id="element-1879">
                    I declare that all information and documents provided are
                    true and accurate to the best of my knowledge. I understand
                    that false claims may lead to legal action.
                  </span>
                </label>
              </div>

              <div className="mt-8" data-id="element-1880">
                <button onClick={handleNext} disabled={!acceptedDeclaration || isSubmitting} className="w-full py-4 rounded-xl font-bold text-lg bg-[#0D3B3B] text-[#FFD700] shadow-lg shadow-[#0D3B3B]/20 active:scale-95 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none transition-all flex items-center justify-center gap-2" data-id="element-1881">
                  {isSubmitting ? <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" data-id="element-1882" /> : 'Submit Claim'}
                </button>
              </div>
            </motion.div>}

          {/* STEP 4: Success & Tracking */}
          {step === 4 && <motion.div key="step4" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex-1 flex flex-col" data-id="element-1883">
              <div className="text-center mb-8" data-id="element-1884">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-500 mx-auto mb-4 border-4 border-white shadow-lg shadow-emerald-500/20" data-id="element-1885">
                  <CheckCircleIcon className="w-10 h-10" data-id="element-1886" />
                </div>
                <h2 className="text-2xl font-black text-[#0D3B3B] mb-2" data-id="element-1887">
                  Claim Submitted!
                </h2>
                <p className="text-sm text-gray-500 leading-relaxed px-4" data-id="element-1888">
                  Your claim has been successfully submitted and is now under
                  review. We're here to support you through this process.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-1889">
                <div className="flex justify-between items-center pb-4 border-b border-gray-50 mb-6" data-id="element-1890">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-1891">
                    Reference
                  </span>
                  <span className="text-sm font-mono font-bold text-[#0D3B3B]" data-id="element-1892">
                    CLM-20260514002
                  </span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-50 mb-6" data-id="element-1893">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-1894">
                    Expected Review
                  </span>
                  <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-1895">
                    3 - 5 Business Days
                  </span>
                </div>

                {/* Visual Timeline */}
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-1896">
                  Claim Status
                </h3>
                <div className="relative pl-6 space-y-6" data-id="element-1897">
                  <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-gray-100" data-id="element-1898" />

                  <div className="relative" data-id="element-1899">
                    <div className="absolute -left-6 w-6 h-6 bg-emerald-500 rounded-full border-4 border-white flex items-center justify-center" data-id="element-1900">
                      <CheckCircleIcon className="w-3 h-3 text-white" data-id="element-1901" />
                    </div>
                    <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-1902">
                      Submitted
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1903">May 14, 2026</p>
                  </div>

                  <div className="relative" data-id="element-1904">
                    <div className="absolute -left-6 w-6 h-6 bg-amber-500 rounded-full border-4 border-white flex items-center justify-center" data-id="element-1905">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse" data-id="element-1906" />
                    </div>
                    <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-1907">
                      Under Review
                    </p>
                    <p className="text-xs text-amber-600 font-medium" data-id="element-1908">
                      Currently processing
                    </p>
                  </div>

                  <div className="relative opacity-50" data-id="element-1909">
                    <div className="absolute -left-6 w-6 h-6 bg-gray-200 rounded-full border-4 border-white" data-id="element-1910" />
                    <p className="text-sm font-bold text-gray-500" data-id="element-1911">
                      Insurer Review
                    </p>
                  </div>

                  <div className="relative opacity-50" data-id="element-1912">
                    <div className="absolute -left-6 w-6 h-6 bg-gray-200 rounded-full border-4 border-white" data-id="element-1913" />
                    <p className="text-sm font-bold text-gray-500" data-id="element-1914">Approved</p>
                  </div>

                  <div className="relative opacity-50" data-id="element-1915">
                    <div className="absolute -left-6 w-6 h-6 bg-gray-200 rounded-full border-4 border-white" data-id="element-1916" />
                    <p className="text-sm font-bold text-gray-500" data-id="element-1917">Paid</p>
                  </div>
                </div>
              </div>

              <div className="mt-auto" data-id="element-1918">
                <button onClick={() => navigate('policies')} className="w-full py-4 rounded-xl font-bold text-lg bg-white border border-gray-200 text-[#0D3B3B] shadow-sm hover:bg-gray-50 active:scale-95 transition-all" data-id="element-1919">
                  Return to Policies
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}
function UploadItem({
  title,
  desc,
  uploaded,
  onUpload
}: {
  title: string;
  desc: string;
  uploaded: boolean;
  onUpload: () => void;
}) {
  return <div className={`p-4 rounded-2xl border flex items-center justify-between transition-colors ${uploaded ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`} data-id="element-1920">
      <div className="flex items-center gap-3" data-id="element-1921">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${uploaded ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-1922">
          <FileTextIcon className="w-5 h-5" data-id="element-1923" />
        </div>
        <div data-id="element-1924">
          <h4 className="font-bold text-sm text-[#0D3B3B]" data-id="element-1925">{title}</h4>
          <p className="text-xs text-gray-500" data-id="element-1926">{desc}</p>
        </div>
      </div>
      {uploaded ? <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center" data-id="element-1927">
          <CheckCircleIcon className="w-5 h-5" data-id="element-1928" />
        </div> : <button onClick={onUpload} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-[#0D3B3B] text-xs font-bold rounded-lg transition-colors flex items-center gap-1" data-id="element-1929">
          <UploadCloudIcon className="w-4 h-4" data-id="element-1930" /> Upload
        </button>}
    </div>;
}